/**
 * Frontend Error Tracking and Submission Monitoring
 * 
 * Features:
 * - Tracks all submission attempts
 * - Logs errors with context
 * - Stores failed submissions for recovery
 * - Monitors submission success/failure
 */

const ERROR_LOG_KEY = 'ats_error_log';
const SUBMISSION_LOG_KEY = 'ats_submission_log';
const FAILED_SUBMISSION_KEY = 'ats_failed_submission';
const MAX_LOG_ENTRIES = 50;

class ErrorTracker {
  /**
   * Log an error with context
   */
  static logError(error, context = {}) {
    const errorEntry = {
      timestamp: new Date().toISOString(),
      type: 'error',
      message: error.message || String(error),
      stack: error.stack,
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }
    };

    this._addToLog(ERROR_LOG_KEY, errorEntry);
    
    // Also log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error tracked:', errorEntry);
    }

    return errorEntry;
  }

  /**
   * Log a submission attempt
   */
  static logSubmissionAttempt(formData, status = 'started') {
    const submissionEntry = {
      id: this._generateSubmissionId(),
      timestamp: new Date().toISOString(),
      status, // 'started', 'success', 'failed'
      email: formData.email,
      name: formData.full_name,
      position: formData.position_applying,
      step: 'submission',
      formDataSnapshot: this._sanitizeFormData(formData)
    };

    this._addToLog(SUBMISSION_LOG_KEY, submissionEntry);
    
    return submissionEntry.id;
  }

  /**
   * Update submission status
   */
  static updateSubmissionStatus(submissionId, status, error = null) {
    const logs = this._getLog(SUBMISSION_LOG_KEY);
    const entry = logs.find(log => log.id === submissionId);
    
    if (entry) {
      entry.status = status;
      entry.completedAt = new Date().toISOString();
      if (error) {
        entry.error = {
          message: error.message,
          stack: error.stack
        };
      }
      
      localStorage.setItem(SUBMISSION_LOG_KEY, JSON.stringify(logs));
    }
  }

  /**
   * Save failed submission for recovery and send to backend
   */
  static saveFailedSubmission(formData, error) {
    const failedSubmission = {
      timestamp: new Date().toISOString(),
      formData: this._sanitizeFormData(formData),
      error: {
        message: error.message || String(error),
        stack: error.stack
      },
      recovered: false
    };

    localStorage.setItem(FAILED_SUBMISSION_KEY, JSON.stringify(failedSubmission));
    
    // Send error report to backend
    this._sendErrorReportToBackend(formData, error).catch(err => {
      console.error('Failed to send error report to backend:', err);
    });
    
    return failedSubmission;
  }

  /**
   * Get failed submission if exists
   */
  static getFailedSubmission() {
    try {
      const data = localStorage.getItem(FAILED_SUBMISSION_KEY);
      if (data) {
        const failed = JSON.parse(data);
        if (!failed.recovered) {
          return failed;
        }
      }
    } catch (e) {
      console.error('Error reading failed submission:', e);
    }
    return null;
  }

  /**
   * Mark failed submission as recovered
   */
  static markFailedSubmissionRecovered() {
    try {
      const data = localStorage.getItem(FAILED_SUBMISSION_KEY);
      if (data) {
        const failed = JSON.parse(data);
        failed.recovered = true;
        localStorage.setItem(FAILED_SUBMISSION_KEY, JSON.stringify(failed));
      }
    } catch (e) {
      console.error('Error marking submission recovered:', e);
    }
  }

  /**
   * Get all error logs
   */
  static getErrorLog() {
    return this._getLog(ERROR_LOG_KEY);
  }

  /**
   * Get all submission logs
   */
  static getSubmissionLog() {
    return this._getLog(SUBMISSION_LOG_KEY);
  }

  /**
   * Get failed submissions from log
   */
  static getFailedSubmissions() {
    const logs = this._getLog(SUBMISSION_LOG_KEY);
    return logs.filter(log => log.status === 'failed');
  }

  /**
   * Export logs for debugging
   */
  static exportLogs() {
    return {
      errors: this.getErrorLog(),
      submissions: this.getSubmissionLog(),
      failedSubmission: this.getFailedSubmission(),
      exportedAt: new Date().toISOString()
    };
  }

  /**
   * Clear all logs (use with caution)
   */
  static clearLogs() {
    localStorage.removeItem(ERROR_LOG_KEY);
    localStorage.removeItem(SUBMISSION_LOG_KEY);
    localStorage.removeItem(FAILED_SUBMISSION_KEY);
  }

  // Private helper methods

  static _addToLog(key, entry) {
    try {
      let logs = this._getLog(key);
      logs.unshift(entry); // Add to beginning
      
      // Keep only last MAX_LOG_ENTRIES
      if (logs.length > MAX_LOG_ENTRIES) {
        logs = logs.slice(0, MAX_LOG_ENTRIES);
      }
      
      localStorage.setItem(key, JSON.stringify(logs));
    } catch (e) {
      console.error('Error adding to log:', e);
      // If localStorage is full, clear old logs
      if (e.name === 'QuotaExceededError') {
        this._clearOldLogs(key);
        // Try again with smaller log
        localStorage.setItem(key, JSON.stringify([entry]));
      }
    }
  }

  static _getLog(key) {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      console.error('Error reading log:', e);
      return [];
    }
  }

  static _clearOldLogs(key) {
    try {
      const logs = this._getLog(key);
      // Keep only last 10 entries
      const trimmed = logs.slice(0, 10);
      localStorage.setItem(key, JSON.stringify(trimmed));
    } catch (e) {
      // If still fails, just remove it
      localStorage.removeItem(key);
    }
  }

  static _generateSubmissionId() {
    return `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static _sanitizeFormData(formData) {
    // Create a safe copy without sensitive data
    const sanitized = { ...formData };
    
    // Keep only essential fields for tracking
    return {
      full_name: sanitized.full_name,
      email: sanitized.email,
      phone: sanitized.phone ? '***-***-' + sanitized.phone.slice(-4) : '',
      city: sanitized.city,
      position_applying: sanitized.position_applying,
      languages_spoken: sanitized.languages_spoken,
      // Don't store full form data for privacy
      hasAllRequiredFields: this._checkRequiredFields(formData)
    };
  }

  static _checkRequiredFields(formData) {
    const required = [
      'full_name',
      'email',
      'phone',
      'city',
      'position_applying',
      'most_recent_job',
      'why_stopped_working',
      'work_authorization',
      'available_days',
      'available_hours'
    ];

    return required.every(field => formData[field] && formData[field] !== '');
  }

  /**
   * Send error report to backend for dashboard notification
   */
  static async _sendErrorReportToBackend(formData, error) {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      if (!API_URL) {
        console.warn('Backend URL not configured, cannot send error report');
        return;
      }

      const errorReport = {
        timestamp: new Date().toISOString(),
        error_type: error.name || 'UnknownError',
        error_message: error.message || String(error),
        candidate_email: formData.email || 'unknown@example.com',
        candidate_name: formData.full_name || 'Unknown',
        candidate_phone: formData.phone || '',
        position_applying: formData.position_applying || 'Unknown',
        submission_id: this._generateSubmissionId(),
        browser_info: navigator.userAgent,
        error_stack: error.stack || '',
        form_step: 'submission',
        http_status: error.response?.status || null
      };

      const response = await fetch(`${API_URL}/api/error-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(errorReport),
        // Don't fail if this request fails
        signal: AbortSignal.timeout(5000) // 5 second timeout
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Error report sent to backend:', result);
        
        if (result.dashboard_notified) {
          console.log('✅ HR dashboard has been notified of the failed submission');
        }
      } else {
        console.warn('Error report sent but received non-OK response:', response.status);
      }
    } catch (err) {
      // Silently fail - we don't want error reporting to cause more errors
      console.error('Could not send error report to backend:', err);
    }
  }
}

// Global error handler
window.addEventListener('error', (event) => {
  ErrorTracker.logError(event.error || event, {
    type: 'unhandled_error',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  });
});

// Unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  ErrorTracker.logError(event.reason, {
    type: 'unhandled_rejection',
    promise: 'Promise rejection'
  });
});

export default ErrorTracker;
